import React, { useState } from 'react';
import { Copy, Check, Lock, Unlock } from 'lucide-react';
import { ColorPalette, hslToHex, hexToRgb } from '../utils/colorGenerator';

interface ColorPaletteDisplayProps {
  palette: ColorPalette;
  onColorLock?: (index: number, locked: boolean) => void;
  lockedColors?: boolean[];
}

export const ColorPaletteDisplay: React.FC<ColorPaletteDisplayProps> = ({ 
  palette, 
  onColorLock,
  lockedColors = []
}) => {
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);

  const copyToClipboard = (color: string, index: number) => {
    const hexColor = hslToHex(color);
    navigator.clipboard.writeText(hexColor);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  const toggleColorLock = (index: number, e: React.MouseEvent) => {
    e.stopPropagation();
    if (onColorLock) {
      onColorLock(index, !lockedColors[index]);
    }
  };

  return (
    <div className="w-full max-w-5xl mx-auto">
      <div className="grid grid-cols-5 gap-0 rounded-xl overflow-hidden shadow-2xl mb-8 border border-gray-200">
        {palette.colors.map((color, index) => (
          <div
            key={index}
            className="relative group h-40 cursor-pointer transition-all duration-300 hover:scale-105 hover:z-10"
            style={{ backgroundColor: color }}
            onClick={() => copyToClipboard(color, index)}
          >
            {/* Lock/Unlock Button */}
            {onColorLock && (
              <button
                onClick={(e) => toggleColorLock(index, e)}
                className={`absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200 ${
                  lockedColors[index] 
                    ? 'bg-white bg-opacity-90 text-gray-700 shadow-md' 
                    : 'bg-black bg-opacity-20 text-white opacity-0 group-hover:opacity-100'
                }`}
              >
                {lockedColors[index] ? <Lock size={14} /> : <Unlock size={14} />}
              </button>
            )}

            {/* Copy Overlay */}
            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-300 flex items-center justify-center">
              <div className="opacity-0 group-hover:opacity-100 transition-all duration-300 text-white text-center transform translate-y-2 group-hover:translate-y-0">
                {copiedIndex === index ? (
                  <div className="flex flex-col items-center">
                    <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center mb-2">
                      <Check size={20} />
                    </div>
                    <span className="text-sm font-medium">Copied!</span>
                  </div>
                ) : (
                  <div className="flex flex-col items-center">
                    <div className="w-10 h-10 bg-white bg-opacity-20 rounded-full flex items-center justify-center mb-2 backdrop-blur-sm">
                      <Copy size={20} />
                    </div>
                    <span className="text-sm font-medium">Click to copy</span>
                  </div>
                )}
              </div>
            </div>

            {/* Color Info */}
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black via-black to-transparent bg-opacity-60 p-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              <div className="text-white text-xs font-mono">
                {hslToHex(color)}
              </div>
            </div>
          </div>
        ))}
      </div>
      
      {/* Color Details */}
      <div className="grid grid-cols-5 gap-4 text-center">
        {palette.colors.map((color, index) => (
          <div key={index} className="space-y-2">
            <div className="font-semibold text-gray-800 text-sm">
              {palette.names[index]}
              {lockedColors[index] && (
                <Lock size={12} className="inline ml-1 text-gray-500" />
              )}
            </div>
            <div className="space-y-1">
              <div className="font-mono text-xs text-gray-600 bg-gray-100 px-2 py-1 rounded">
                {hslToHex(color)}
              </div>
              <div className="font-mono text-xs text-gray-500">
                {color.match(/hsl\((\d+),\s*(\d+)%,\s*(\d+)%\)/) && 
                  `HSL(${color.match(/hsl\((\d+),\s*(\d+)%,\s*(\d+)%\)/)![1]}, ${color.match(/hsl\((\d+),\s*(\d+)%,\s*(\d+)%\)/)![2]}%, ${color.match(/hsl\((\d+),\s*(\d+)%,\s*(\d+)%\)/)![3]}%)`
                }
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};